import React, { useState, useEffect } from 'react';
import { atom, useRecoilValue, RecoilValue, RecoilValueReadOnly } from 'recoil';
import { v4 as uuid } from 'uuid';
import { css, SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';

import { icons } from '@assets/icons';
import { Input } from '@components/atoms';

export type FilterLabel = string;
export type FilterValue = string;
export type FilterSet = [FilterLabel, FilterValue];

export type HeaderType = {
  label: string;
  data_label: string;
  sorting?: boolean;
  filtering?: FilterSet[];
};

export type ShowType = {
  checkboxCol?: boolean;
  numberCol?: boolean;
};

export type DataType<T> = {
  dataArray?: T[];
  dataState?: RecoilValueReadOnly<T[]> | RecoilValue<T[]>;
};

export type TableStyleProps = {
  styled?: SerializedStyles;
};
export type BaseTableType<T = any> = {
  headers?: HeaderType[];
  data: DataType<T>;
  customColumn?: {
    [key: string]: (row: T) => React.JSX.Element;
  };
  highlightColumn?: string;
  sortCallback?: any;
  isShow?: ShowType;
  styles?: {
    table?: SerializedStyles;
    theader?: SerializedStyles;
    tbody?: SerializedStyles;
    tfooter?: SerializedStyles;
  };
  onClickWorkListItem?: (item: any) => void;
  searchConditions?: any;
  setSearchConditions?: React.Dispatch<React.SetStateAction<T>>;
  includeSearch?: boolean;
};

export const worklistState = atom<any[]>({
  key: 'worklistState',
  default: [],
});

const Worklist = ({
  headers, //
  data, //productUsers
  customColumn = {}, //
  isShow,
  styles = {},
  highlightColumn, // last_update
  onClickWorkListItem,
  includeSearch,
}: BaseTableType) => {
  const { dataArray, dataState } = data;
  const worklistItems = useRecoilValue(dataState || worklistState);
  const [displayList, setDisplayList] = useState<any[]>([]); // 받아온 데이터 원본
  const [valueState, setValueState] = useState({
    id: '', //  // searchType: searchValue,
    company: '',
  });
  const [disabled, setDisabled] = useState(true);

  const renderOptionalColumns = (index?: string) => {
    return (
      <>
        {isShow?.checkboxCol && (
          <TableCol data-coltype={'checkbox'} data-index={index || 'all'}>
            <input type={'checkbox'} />
          </TableCol>
        )}
        {isShow?.numberCol && <TableCol data-coltype={'rownum'}>{index || `no`}</TableCol>}
      </>
    );
  };

  const renderTableHeader = () => {
    if (headers) {
      return (
        <TableHeaderRow>
          {renderOptionalColumns()}
          {headers.map((item) => (
            <TableHeadCol key={uuid()} data-label={item.data_label}>
              {item.label}
            </TableHeadCol>
          ))}
        </TableHeaderRow>
      );
    }

    if (dataArray && dataArray.length > 0) {
      return (
        <TableHeaderRow>
          {renderOptionalColumns()}
          {Object.keys(dataArray[0]).map((value) => (
            <TableHeadCol key={uuid()}>{value}</TableHeadCol>
          ))}
        </TableHeaderRow>
      );
    }
  };

  const renderTableBody = () => {
    if (headers) {
      if (includeSearch) {
        // receiving화면
        if (displayList.length === 0) {
          return 'No matching search results found.';
        }
        return displayList.map((row: any, index: number) => (
          <TableRow
            key={uuid()}
            data-highlight={`${highlightColumn && row[highlightColumn]}`}
            data-row={`${JSON.stringify(row).replace(/"/gi, "'")}`}
          >
            {renderOptionalColumns(`${index + 1}`)}

            {headers.map((header: any) => {
              return (
                <TableCol key={uuid()} data-label={`${header.data_label}`} data-value={row[`${header.data_label}`]}>
                  {row[`${header.data_label}`]}
                </TableCol>
              );
            })}
          </TableRow>
        ));
      }
      return displayList.map((row: any, index: number) => (
        <TableRow
          key={uuid()}
          data-highlight={`${highlightColumn && row[highlightColumn]}`}
          data-row={`${JSON.stringify(row).replace(/"/gi, "'")}`}
          onClick={() => onClickWorkListItem && onClickWorkListItem(row)}
        >
          {renderOptionalColumns(`${index + 1}`)}
          {headers.map((header: any) => {
            if (customColumn[`${header.data_label}`]) {
              return (
                <TableCol key={uuid()} data-label={`${header.data_label}`} data-value={row[`${header.data_label}`]}>
                  {customColumn[`${header.data_label}`](row)}
                </TableCol>
              );
            }
            return (
              <TableCol key={uuid()} data-label={`${header.data_label}`} data-value={row[`${header.data_label}`]}>
                {header.data_label === 'products'
                  ? String(row[`${header.data_label}`]).replaceAll(',', '\n')
                  : row[`${header.data_label}`]}
              </TableCol>
            );
          })}
        </TableRow>
      ));
    }
    return displayList.map((row: any, index: number) => (
      <TableRow key={uuid()}>
        {renderOptionalColumns(`${index + 1}`)}
        {Object.keys(row).map((column: any) => (
          <TableCol key={uuid()} data-value={row[`${column}`]}>
            {row[`${column}`]}
          </TableCol>
        ))}
      </TableRow>
    ));
  };

  const setFilterData = (e: any, label: string) => {
    if (label === 'ID') {
      setValueState((prev) => ({ ...prev, id: e.target.value }));
    }
    if (label === 'Company') {
      setValueState((prev) => ({ ...prev, company: e.target.value }));
    }
  };

  const onClickClearIcon = (e: any) => {
    if (e.target.className === 'ID') {
      setValueState((prev) => ({ ...prev, id: '' }));
      if (!dataArray) return;

      const filterList = dataArray.filter((row) => {
        return row.companyName.includes(valueState.company);
      });
      setDisplayList(filterList);
    } else if (e.target.className === 'Company') {
      setValueState((prev) => ({ ...prev, company: '' }));
      if (!dataArray) return;

      const filterList = dataArray.filter((row) => {
        return row.companyName.includes(valueState.id);
      });
      setDisplayList(filterList);
    }
  };

  useEffect(() => {
    if (!includeSearch || !dataArray) return;

    if (disabled) {
      setDisabled(false);
      return;
    }

    const filterList = dataArray.filter((row) => {
      return String(row.companyId).includes(valueState.id) && row.companyName.includes(valueState.company);
    });

    setDisplayList(filterList);
  }, [valueState.id, valueState.company]);

  useEffect(() => {
    if (!includeSearch) return;
    renderTableBody();
  }, [displayList]);

  useEffect(() => {
    if (dataArray) {
      setDisplayList([...dataArray]);
    } else if (dataState) {
      setDisplayList([...worklistItems]);
    }
  }, [dataArray, dataState, worklistItems]);

  return (
    <Table styled={styles.table}>
      <TableHeader styled={styles.theader}>{renderTableHeader()}</TableHeader>
      <TableBody styled={styles.tbody} style={{ backgroundColor: '#fff' }}>
        {includeSearch &&
          headers &&
          headers.map((header: any, i) => {
            if (header.label === 'ID') {
              return (
                <TableCol key={i} data-label={`${header.data_label}`}>
                  <Input
                    type={'text'}
                    placeholder={'Search'}
                    value={header.label === 'ID' ? valueState.id : valueState.company}
                    onChange={(e) => {
                      setFilterData(e, header.label);
                    }}
                  />
                  {valueState.id && (
                    <img
                      className={header.label}
                      src={icons.deleteIcon}
                      alt="clear"
                      onClick={(e) => onClickClearIcon(e)}
                    />
                  )}
                </TableCol>
              );
            }
            if (header.label === 'Company') {
              return (
                <TableCol key={i} data-label={`${header.data_label}`}>
                  <Input
                    type={'text'}
                    placeholder={'Search'}
                    value={header.label === 'ID' ? valueState.id : valueState.company}
                    onChange={(e) => {
                      setFilterData(e, header.label);
                    }}
                  />
                  {valueState.company && (
                    <img
                      className={header.label}
                      src={icons.deleteIcon}
                      alt="clear"
                      onClick={(e) => onClickClearIcon(e)}
                    />
                  )}
                </TableCol>
              );
            }

            return <TableCol key={uuid()} data-label={`${header.data_label}`}></TableCol>;
          })}
        {displayList.length > 0 && renderTableBody()}
        {displayList.length === 0 && headers && (
          <tr data-row="no-data">
            <td colSpan={headers.length}>
              <b>No Data!</b>
            </td>
          </tr>
        )}
      </TableBody>
      <TableFooter styled={styles.tfooter} />
    </Table>
  );
};

//#region Worklist Styled Component
const RowDefaultCss = css`
  box-sizing: border-box;
  height: 40px;
`;

const Table = styled.table`
  border-spacing: 1px;
  font-family: Noto Sans KR;
  font-style: normal;
  font-size: 14px;
  color: #555555;
  line-height: 20px;
  text-align: center;

  @media (max-width: 412px) {
    thead {
      display: grid;
      tr {
        display: grid;
        grid-gap: 1px;
        grid-template-columns: 1fr 1fr;
        height: unset;
        background-color: unset;
        border-bottom: unset;
        th {
          background-color: #7fe200;
        }
      }
    }
    tbody {
      display: grid;
      tr {
        display: grid;
        grid-gap: 1px;
        grid-template-columns: 1fr 1fr;
        height: unset;
        background-color: unset;
        &:nth-of-type(n + 1) {
          margin-top: 10px;
        }
        td {
          min-height: 20px;
        }
        &[data-row='no-data' i] {
          grid-template-columns: 1fr;
        }
      }
    }
  }

  ${(props: TableStyleProps) => props.styled};
`;
const TableHeader = styled.thead`
  font-weight: bold;
  color: #ffffff;
  ${(props: TableStyleProps) => props.styled};
`;
const TableHeaderRow = styled.tr`
  ${RowDefaultCss};
  background-color: #00bf00;
  border-bottom: 1px solid #00bf00;
`;
const TableHeadCol = styled.th`
  padding: 0 5px;
`;
const TableBody = styled.tbody`
  tr[data-row='no-data' i] {
    height: 650px;
    td {
      margin: auto;
      justify-content: center;
      align-items: center;
      font-weight: bold;
      font-size: 24px;
      line-height: 35px;
      color: #999999;
      & > b {
        display: flex;
        flex-direction: column;
        &:before {
          content: url(${icons.nodata});
        }
      }
    }
  }
  ${(props: TableStyleProps) => props.styled};
`;
const TableFooter = styled.tfoot`
  ${(props: TableStyleProps) => props.styled};
`;
const TableRow = styled.tr`
  ${RowDefaultCss}

  background-color: #ffffff;

  &[data-row*='status': 'Waiting' i] {
    color: #999999;

    & > td {
      background: #fafafa;
    }
  }
`;
const TableCol = styled.td`
  position: relative;
  padding: 6px 5px;
  white-space: pre-line;
  &[data-coltype='checkbox'],
  &[data-coltype='rownum'] {
    text-transform: uppercase;
    width: 40px;
  }
  &[data-label='no'] {
    width: 52px;
  }
  &[data-label='companyId'] {
    width: 180px;
  }
  &[data-label='companyName'],
  &[data-label='createdDatetime'] {
    width: 240px;
  }
  &[data-label='companyId'],
  &[data-label='companyName'],
  &[data-label='no'],
  &[data-label='createdDatetime'] {
    height: 40px;
    padding: 5px 5px 1px 3px;
    box-sizing: border-box;
    & > input {
      box-sizing: border-box;
    }
  }
  &[data-label='status'] + td[data-label='delete'] {
    & > div {
      display: flex;
      align-items: center;
      justify-content: center;
      :before {
        content: '-';
      }
      button {
        display: none;
      }
    }
  }
  &[data-label='status'][data-value='Waiting' i] + td[data-label='delete'] {
    & > div {
      :before {
        content: '';
      }
      button {
        display: block;
      }
    }
  }

  & > img {
    &.ID,
    &.Company {
      position: absolute;
      /* top: 0; */
      bottom: 16px;
      right: 10px;
      /* margin: auto 0; */
      cursor: pointer;
    }
  }
`;

export default Worklist;
